<template>
  <v-dialog v-model="isVisible" max-width="480" data-cy="push-error-dialog">
    <v-card>
      <v-card-title class="text-h6 font-weight-bold">Submit Error Details</v-card-title>
      <v-card-text>
        <!-- Error summary -->
        <div class="error-detail-box mb-4">
          <strong class="d-block mb-1">{{ lien?.vendorDisplayName }}</strong>
          <span>{{ message }}</span>
        </div>

        <div class="text-body-2 font-weight-bold text-grey-darken-3 mb-2">
          Suggested resolution steps:
        </div>
        <ol class="error-steps">
          <li v-for="(step, i) in steps" :key="i">{{ step }}</li>
        </ol>
      </v-card-text>
      <v-card-actions class="pa-4">
        <LlSecondaryButton data-cy="push-error-close" @click="close">
          Close
        </LlSecondaryButton>
        <v-spacer />
        <LlSecondaryButton
          v-if="canRetry"
          color="primary"
          data-cy="push-error-retry"
          @click="retry"
        >
          <QboIcon :size="16" class="mr-2" />
          Retry Submit
        </LlSecondaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { LienData } from '@/models/lienTypes';
import { resolveQboPushError, resolveQboPushSteps } from '@/utils/lienHelpers';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import QboIcon from '@/components/QboIcon.vue';

const isVisible = ref(false);
const lien = ref<LienData | null>(null);
let resolvePromise: ((value: 'retry' | 'close') => void) | null = null;

const message = computed(() => resolveQboPushError(lien.value?.qboPushError, lien.value?.qboPushError));
const steps = computed(() => resolveQboPushSteps(lien.value?.qboPushError));
// Reconciled checks cannot be retried, nor can a lien trust can't cover yet (same gate as canRetryLien).
const canRetry = computed(
  () => lien.value?.qboPushError !== 'RECONCILED_CHECK' && !lien.value?.pushBlockedReason,
);

/** Open the error detail for a failed lien; resolves 'retry' or 'close'. */
function open(target: LienData): Promise<'retry' | 'close'> {
  lien.value = target;
  isVisible.value = true;
  return new Promise((resolve) => {
    resolvePromise = resolve;
  });
}

function close(): void {
  isVisible.value = false;
  resolvePromise?.('close');
  resolvePromise = null;
}

function retry(): void {
  isVisible.value = false;
  resolvePromise?.('retry');
  resolvePromise = null;
}

defineExpose({ open });
</script>

<style scoped>
.error-detail-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #991b1b;
  line-height: 1.5;
}
.error-steps {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #475569;
  line-height: 1.7;
}
.error-steps li {
  margin-bottom: 4px;
}
</style>
